<template>
  <div>
    <!-- 添加弹窗 -->
    <a-modal
      title="添加推荐保函订单"
      :visible="visible1"
      :footer="null"
      @cancel="handleCancel"
    >
      <!-- 单笔添加 -->
      <div class="add-section">
        <div class="section-label">单笔添加</div>
        <a-input
          v-model="formData.code"
          placeholder="请输入保函编号或订单号"
          allowClear
          @pressEnter="handleOk"
        />
        <div class="tip-text">
          请仔细核对需要添加的保函编号或订单号，若多次输入错误会被禁用此功能。
        </div>
        <a-button type="primary" :loading="adding" :disabled="importing" @click="handleOk" style="margin-top:10px;float:right;margin-bottom:12px;">确认添加</a-button>
      </div>

      <!-- 分割线 -->
      <a-divider/>

      <!-- 批量导入 -->
      <div class="batch-section">
        <div class="section-label">批量添加</div>
        <div class="batch-actions">
          <input
            ref="fileInput"
            type="file"
            accept=".xlsx,.xls"
            style="display: none"
            @change="handleFileImport"
          />
          <a-button @click="downloadTemplate" icon="download">
            下载模板
          </a-button>
          <a-button type="primary" :loading="importing" :disabled="adding" @click="$refs.fileInput.click()" icon="upload">
            导入Excel
          </a-button>
        </div>
        <div class="batch-steps">
          <div class="step">
            <span class="step-num">①</span> 下载模板，在 Excel 第一列填入订单编号
          </div>
          <div class="step">
            <span class="step-num">②</span> 上传填写好的 Excel 文件
          </div>
        </div>
      </div>
    </a-modal>

    <!-- 导入结果弹窗 -->
    <a-modal
      title="导入结果"
      :visible="resultVisible"
      :footer="null"
      width="560px"
      @cancel="resultVisible = false"
    >
      <div v-if="resultItems.length > 0">
        <!-- 统计卡片 -->
        <div class="result-summary">
          <div class="stat-item">
            <span class="stat-num">{{ resultItems.length }}</span>
            <span class="stat-label">总计</span>
          </div>
          <div class="stat-item stat-success">
            <span class="stat-num">{{ successCount }}</span>
            <span class="stat-label">成功</span>
          </div>
          <div v-if="failCount > 0" class="stat-item stat-fail">
            <span class="stat-num">{{ failCount }}</span>
            <span class="stat-label">失败</span>
          </div>
        </div>

        <!-- 失败明细 -->
        <div v-if="failCount > 0" class="fail-section">
          <div class="section-title">失败明细</div>
          <a-table
            :columns="resultColumns"
            :data-source="failedList"
            :pagination="false"
            :scroll="{ x: 480, y: 380 }"
            size="small"
            row-key="code"
          />
        </div>
        <a-alert v-else type="success" show-icon message="全部导入成功" style="margin-top:16px;" />
      </div>
    </a-modal>
  </div>
</template>

<script>
import { isSessionExpiredError } from '@/utils/session';
import { add_p_contract, batch_add_p_contract } from '@/api/platform';
import * as XLSX from 'xlsx';

export default {
  props: {
    visible1: Boolean,
  },
  data() {
    return {
      formData: {
        code: '',
      },
      adding: false,
      importing: false,
      resultVisible: false,
      resultItems: [],
      resultColumns: [
        { title: '订单编号', dataIndex: 'code', width: 200 },
        { title: '失败原因', dataIndex: 'error' },
      ],
    };
  },
  computed: {
    successCount() {
      return this.resultItems.filter((i) => i.success).length;
    },
    failCount() {
      return this.resultItems.filter((i) => !i.success).length;
    },
    failedList() {
      return this.resultItems.filter((i) => !i.success);
    },
  },
  methods: {
    async handleOk() {
      if (this.adding || this.importing) return;
      if (!this.formData.code.trim()) {
        this.$message.warning('请输入保函编号或订单号');
        return;
      }
      this.adding = true;
      try {
        await add_p_contract({ code: this.formData.code.trim() });
        this.$message.success('添加成功');
        this.$emit('add-success');
        this.formData = { code: '' };
        this.$emit('update:visible1', false);
      } catch (err) {
        if (!isSessionExpiredError(err)) this.$message.error(err.message || err);
      } finally {
        this.adding = false;
      }
    },
    handleCancel() {
      this.formData = { code: '' };
      this.$emit('update:visible1', false);
    },
    async handleFileImport(e) {
      if (this.adding || this.importing) return;
      const file = e.target.files[0];
      if (!file) return;

      this.importing = true;
      try {
        // 解析 Excel
        const data = await this.readExcel(file);
        if (data.length === 0) {
          this.$message.warning('未读取到有效数据');
          return;
        }

        // 提取编码列表（取第一列）
        const codes = data.map((row) => String(row[Object.keys(row)[0]] || '').trim()).filter(Boolean);
        if (codes.length === 0) {
          this.$message.warning('Excel 中无有效订单编号');
          return;
        }

        // 调用批量接口
        const res = await batch_add_p_contract({ codes });
        this.resultItems = res.items || [];

        this.$emit('update:visible1', false);

        if (res.fail === 0) {
          this.$message.success(`全部导入成功，共 ${res.success} 条`);
          this.$emit('add-success');
        } else {
          this.resultVisible = true;
          if (res.success > 0) {
            this.$emit('add-success');
          }
        }
      } catch (err) {
        if (!isSessionExpiredError(err)) this.$message.error(err.message || err);
      } finally {
        this.importing = false;
        // 重置文件选择器，允许重复选择同一文件
        e.target.value = '';
      }
    },
    downloadTemplate() {
      const ws = XLSX.utils.aoa_to_sheet([['保函编号/订单号']]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '批量添加推荐订单');
      XLSX.writeFile(wb, '批量添加推荐订单模板.xlsx');
    },
    readExcel(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const wb = XLSX.read(e.target.result, { type: 'binary' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
            resolve(data);
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = reject;
        reader.readAsBinaryString(file);
      });
    },
  },
};
</script>

<style scoped>
.add-section {
  margin-bottom: 4px;
}
.section-label {
  font-size: 15px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}
.tip-text {
  color: #999;
  font-size: 12px;
  margin-top: 6px;
}

.batch-actions {
  display: flex;
  gap: 10px;
}
.batch-steps {
  margin-top: 10px;
}
.step {
  font-size: 13px;
  color: #555;
  line-height: 2;
}
.step-num {
  display: inline-block;
  width: 22px;
  height: 22px;
  line-height: 22px;
  text-align: center;
  background: #f0f0f0;
  border-radius: 50%;
  font-size: 12px;
  margin-right: 6px;
  vertical-align: middle;
}

.result-summary {
  display: flex;
  gap: 12px;
  justify-content: center;
}
.stat-item {
  text-align: center;
  padding: 4px;
  border-radius: 8px;
  background: #fafafa;
  min-width: 80px;
}
.stat-item.stat-success {
  background: #f6ffed;
  border: 1px solid #b7eb8f;
}
.stat-item.stat-fail {
  background: #fff2f0;
  border: 1px solid #ffccc7;
}
.stat-num {
  display: block;
  font-size: 28px;
  font-weight: 600;
  line-height: 1.2;
}
.stat-label {
  display: block;
  font-size: 13px;
  color: #999;
  margin-top: 2px;
}

.fail-section {
  margin-top: 20px;
}
.section-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 10px;
  padding-left: 8px;
  border-left: 3px solid #ff4d4f;
}
</style>
